import { apiClient } from "./base/http";

export interface Customer {
  id: string;
  user_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  gender: "male" | "female" | "other" | null;
  birthday: string | null;
  address: string | null;
  note: string | null;
  membership_level: "normal" | "vip" | "vvip";
  total_spent: number;
  visit_count: number;
  last_visit_date: string | null;
  tags: string[] | null;
  is_active: boolean;
  /** 設備租借審核：營運設定開啟「設備租借需審核顧客」時，要核准才看得到前台租借入口 */
  device_rental_approved?: boolean;
  loyalty_points?: number;
  line_user_id?: string | null;
  line_display_name?: string | null;
  line_picture_url?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerPayload {
  name: string;
  phone?: string;
  email?: string;
  gender?: "male" | "female" | "other";
  birthday?: string;
  address?: string;
  note?: string;
  membership_level?: "normal" | "vip" | "vvip";
  tags?: string[];
}

export interface UpdateCustomerPayload {
  name?: string;
  phone?: string;
  email?: string;
  gender?: "male" | "female" | "other";
  birthday?: string;
  address?: string;
  note?: string;
  membership_level?: "normal" | "vip" | "vvip";
  is_active?: boolean;
  device_rental_approved?: boolean;
  tags?: string[];
}

export interface GetCustomersParams {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
  date_from?: string;
  date_to?: string;
  tag?: string;
}

export interface CustomersResponse {
  success: boolean;
  customers: Customer[];
  total: number;
  message?: string;
}

export interface CustomerResponse {
  success: boolean;
  customer?: Customer;
  message?: string;
}

export const fetchCustomers = async (params?: GetCustomersParams): Promise<Customer[]> => {
  const { data } = await apiClient.get<CustomersResponse>("/api/customers", { params });
  if (!data.success) throw new Error(data.message || "取得客戶失敗");
  return data.customers;
};

export const fetchCustomersPaged = async (params?: GetCustomersParams): Promise<{ customers: Customer[]; total: number }> => {
  const { data } = await apiClient.get<CustomersResponse>("/api/customers", { params });
  if (!data.success) throw new Error(data.message || "取得客戶失敗");
  return { customers: data.customers, total: data.total };
};

export const fetchAllCustomersForAnalytics = async (
  params?: Omit<GetCustomersParams, "page" | "limit">,
  pageSize = 200,
): Promise<Customer[]> => {
  const first = await fetchCustomersPaged({ ...params, page: 1, limit: pageSize });
  const totalPages = Math.ceil(first.total / pageSize);
  if (totalPages <= 1) return first.customers;

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      fetchCustomersPaged({ ...params, page: i + 2, limit: pageSize })
    )
  );

  return [first.customers, ...rest.map((r) => r.customers)].flat();
};

export const fetchCustomer = async (id: string): Promise<Customer> => {
  const { data } = await apiClient.get<CustomerResponse>(`/api/customers/${id}`);
  if (!data.success || !data.customer) throw new Error(data.message || "取得客戶失敗");
  return data.customer;
};

export const createCustomer = async (payload: CreateCustomerPayload): Promise<Customer> => {
  const { data } = await apiClient.post<CustomerResponse>("/api/customers", payload);
  if (!data.success || !data.customer) throw new Error(data.message || "建立客戶失敗");
  return data.customer;
};

export const updateCustomer = async (id: string, payload: UpdateCustomerPayload): Promise<Customer> => {
  const { data } = await apiClient.put<CustomerResponse>(`/api/customers/${id}`, payload);
  if (!data.success || !data.customer) throw new Error(data.message || "更新客戶失敗");
  return data.customer;
};

export const deleteCustomer = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/customers/${id}`);
};

// ─── Customer Tag Presets (shared tags) ───

export const fetchCustomerTagPresets = async (): Promise<string[]> => {
  const { data } = await apiClient.get<{ success: boolean; tags: string[] }>("/api/customers/tags/presets");
  return data.tags;
};

export const createCustomerTagPreset = async (name: string): Promise<void> => {
  await apiClient.post("/api/customers/tags/presets", { name });
};

export const deleteCustomerTagPreset = async (name: string): Promise<void> => {
  await apiClient.delete(`/api/customers/tags/presets/${encodeURIComponent(name)}`);
};

// ─── Customer Packages ───

export interface CustomerPackage {
  id: string;
  package_id: string;
  name: string;
  description: string | null;
  total_sessions: number;
  used_sessions: number;
  remaining_sessions: number;
  purchase_date: string;
  expiry_date: string;
  is_active: boolean;
  /** 共用同一組次數的同行者（後台可新增／移除） */
  members?: CustomerPackageMember[];
  /** 套票是否含「多人同時段」服務，才能加同行者 */
  can_share?: boolean;
  /** 同行者上限；null = 不限 */
  companion_limit?: number | null;
  /** 套票涵蓋的服務 ID */
  service_ids?: string[];
  /** 套票內的多人同時段服務 ID（預約這些服務時可帶入共用者） */
  multi_service_ids?: string[];
}

export interface CustomerPackageMember {
  id: string;
  customerId: string;
  name: string;
  phone?: string | null;
}

export interface AddCustomerPackagePayload {
  package_id: string;
  total_sessions?: number;
  expiry_date?: string;
}

export interface UpdateCustomerPackagePayload {
  total_sessions?: number;
  remaining_sessions?: number;
  expiry_date?: string;
  is_active?: boolean;
}

export const fetchCustomerPackages = async (customerId: string): Promise<CustomerPackage[]> => {
  const { data } = await apiClient.get<{ success: boolean; packages: CustomerPackage[] }>(
    `/api/customers/${customerId}/packages`
  );
  return data.packages;
};

export const addCustomerPackage = async (
  customerId: string,
  payload: AddCustomerPackagePayload
): Promise<CustomerPackage> => {
  const { data } = await apiClient.post<{ success: boolean; package: CustomerPackage }>(
    `/api/customers/${customerId}/packages`,
    payload
  );
  return data.package;
};

export const updateCustomerPackage = async (
  customerId: string,
  cpId: string,
  payload: UpdateCustomerPackagePayload
): Promise<CustomerPackage> => {
  const { data } = await apiClient.patch<{ success: boolean; package: CustomerPackage }>(
    `/api/customers/${customerId}/packages/${cpId}`,
    payload
  );
  return data.package;
};

export const deleteCustomerPackage = async (
  customerId: string,
  cpId: string
): Promise<void> => {
  await apiClient.delete(`/api/customers/${customerId}/packages/${cpId}`);
};

export const addCustomerPackageMember = async (
  customerId: string,
  cpId: string,
  memberCustomerId: string
): Promise<CustomerPackageMember> => {
  const { data } = await apiClient.post<{ success: boolean; member: CustomerPackageMember }>(
    `/api/customers/${customerId}/packages/${cpId}/members`,
    { customer_id: memberCustomerId }
  );
  return data.member;
};

export const removeCustomerPackageMember = async (
  customerId: string,
  cpId: string,
  memberId: string
): Promise<void> => {
  await apiClient.delete(`/api/customers/${customerId}/packages/${cpId}/members/${memberId}`);
};

// ─── Customer Coupon History ───

export interface CustomerCouponHistoryItem {
  id: string;
  coupon_id: string;
  code: string;
  name: string | null;
  type: "percent" | "fixed" | "gift";
  value: number;
  gift_description?: string | null;
  min_spend: number | null;
  is_used: boolean;
  used_at: string | null;
  booking_id: string | null;
  expires_at: string | null;
  created_at: string;
}

export const fetchCustomerCouponHistory = async (customerId: string): Promise<CustomerCouponHistoryItem[]> => {
  const { data } = await apiClient.get<{ success: boolean; coupons: CustomerCouponHistoryItem[] }>(
    `/api/customers/${customerId}/coupon-history`
  );
  return data.coupons;
};

export interface CustomerCouponUsageItem {
  id: string;
  coupon_id: string;
  code: string;
  name: string | null;
  type: "percent" | "fixed" | "gift";
  value: number;
  used_at: string | null;
  booking_id: string | null;
  discount_amount: number | null;
  booking_date: string | null;
  /** booking = 預約用券；package = 前台購買套票用券（booking_id 為空） */
  source?: "booking" | "package";
  package_name?: string | null;
  /** 套票訂單狀態：pending = 待店家確認收款，paid = 已發套票 */
  package_order_status?: "pending" | "paid" | null;
}

export const fetchCustomerCouponUsageHistory = async (customerId: string): Promise<CustomerCouponUsageItem[]> => {
  const { data } = await apiClient.get<{ success: boolean; usages: CustomerCouponUsageItem[] }>(
    `/api/customers/${customerId}/coupon-usage-history`
  );
  return data.usages;
};

// ─── Customer Reward Redemptions (紅利兌換活動兌換到的服務/商品，跟優惠券分開) ───

export interface CustomerRewardRedemptionItem {
  id: string;
  campaign_id: string;
  reward_type: "service" | "product";
  target_id: string;
  target_name: string | null;
  code: string;
  name: string;
  value: number;
  is_used: boolean;
  used_at: string | null;
  booking_id: string | null;
  expires_at: string | null;
  created_at: string;
}

/**
 * 顧客還能用的兌換項目（未使用且未過期），給後台新增/編輯預約選用折抵。
 * 編輯既有預約時傳 bookingId，會一併帶回目前已套用在該筆預約上的那張。
 */
export const fetchCustomerRewardRedemptions = async (
  customerId: string,
  bookingId?: string | null
): Promise<CustomerRewardRedemptionItem[]> => {
  const { data } = await apiClient.get<{ success: boolean; redemptions: CustomerRewardRedemptionItem[] }>(
    `/api/customers/${customerId}/reward-redemptions`,
    { params: bookingId ? { booking_id: bookingId } : undefined }
  );
  return data.redemptions;
};

export interface UpdateCustomerCouponPayload {
  expires_at?: string;
  is_used?: boolean;
  min_spend?: number;
}

export const updateCustomerCoupon = async (
  customerId: string,
  ccId: string,
  payload: UpdateCustomerCouponPayload
): Promise<CustomerCouponHistoryItem> => {
  const { data } = await apiClient.patch<{ success: boolean; coupon: CustomerCouponHistoryItem }>(
    `/api/customers/${customerId}/coupons/${ccId}`,
    payload
  );
  return data.coupon;
};

export const deleteCustomerCoupon = async (customerId: string, ccId: string): Promise<void> => {
  await apiClient.delete(`/api/customers/${customerId}/coupons/${ccId}`);
};

// ─── Customer Asset Transfers (coupons / packages) ───

export interface TransferAssetPayload {
  to_customer_id: string;
  note?: string;
}

export interface AssetTransferResult {
  id: string;
  createdAt: string;
}

export const transferCustomerCoupon = async (
  customerId: string,
  ccId: string,
  payload: TransferAssetPayload
): Promise<{ coupon: CustomerCouponHistoryItem; transfer: AssetTransferResult }> => {
  const { data } = await apiClient.post<{
    success: boolean;
    coupon: CustomerCouponHistoryItem;
    transfer: AssetTransferResult;
  }>(`/api/customers/${customerId}/coupons/${ccId}/transfer`, payload);
  return { coupon: data.coupon, transfer: data.transfer };
};

export const transferCustomerPackage = async (
  customerId: string,
  cpId: string,
  payload: TransferAssetPayload
): Promise<{ package: CustomerPackage; transfer: AssetTransferResult }> => {
  const { data } = await apiClient.post<{
    success: boolean;
    package: CustomerPackage;
    transfer: AssetTransferResult;
  }>(`/api/customers/${customerId}/packages/${cpId}/transfer`, payload);
  return { package: data.package, transfer: data.transfer };
};

// ─── Customer Ratings (cross-tenant anonymous) ───

export interface CustomerRating {
  id: string;
  phone: string;
  user_id: string;
  rating: number;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface AnonymousRating {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
}

export interface CustomerRatingsResponse {
  success: boolean;
  my_ratings: CustomerRating[];
  other_ratings: AnonymousRating[];
  avg_rating: number | null;
  total_count: number;
}

export interface CreateCustomerRatingPayload {
  phone: string;
  rating: number;
  comment?: string;
}

export const fetchCustomerRatings = async (phone: string): Promise<CustomerRatingsResponse> => {
  const { data } = await apiClient.get<CustomerRatingsResponse>("/api/customer-ratings", {
    params: { phone },
  });
  return data;
};

export const createCustomerRating = async (payload: CreateCustomerRatingPayload): Promise<CustomerRating> => {
  const { data } = await apiClient.post<{ success: boolean; rating: CustomerRating }>(
    "/api/customer-ratings",
    payload
  );
  return data.rating;
};

export const deleteCustomerRating = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/customer-ratings/${id}`);
};

export const recalculateCustomerStats = async (): Promise<void> => {
  await apiClient.post("/api/customers/recalculate-stats");
};

// ─── Loyalty Points ───

export type LoyaltyTransactionType = "earn" | "redeem" | "expire" | "adjust" | "gift" | "reverse";

export interface LoyaltyPointTransaction {
  id: string;
  customer_id: string;
  type: LoyaltyTransactionType;
  points: number;
  balance_after: number;
  description?: string | null;
  booking_id?: string | null;
  booking_amount?: number | null;
  created_at: string;
}

export interface LoyaltyPointsResponse {
  success: boolean;
  balance: number;
  transactions: LoyaltyPointTransaction[];
}

export interface RedeemLoyaltyPointsPayload {
  points: number;
  booking_id?: string;
  description?: string;
}

export const fetchCustomerLoyaltyPoints = async (
  customerId: string,
  params?: { limit?: number; offset?: number }
): Promise<LoyaltyPointsResponse> => {
  const { data } = await apiClient.get<LoyaltyPointsResponse>(
    `/api/customers/${customerId}/loyalty-points`,
    { params }
  );
  return data;
};

export interface AdjustLoyaltyPointsPayload {
  amount: number;
  note?: string;
}

export const adjustCustomerLoyaltyPoints = async (
  customerId: string,
  payload: AdjustLoyaltyPointsPayload
): Promise<{ success: boolean; balance: number }> => {
  const { data } = await apiClient.post<{ success: boolean; balance: number }>(
    `/api/customers/${customerId}/loyalty-points/adjust`,
    payload
  );
  return data;
};

export const redeemCustomerLoyaltyPoints = async (
  customerId: string,
  payload: RedeemLoyaltyPointsPayload
): Promise<{ success: boolean; transaction: LoyaltyPointTransaction; balance: number }> => {
  const { data } = await apiClient.post<{ success: boolean; transaction: LoyaltyPointTransaction; balance: number }>(
    `/api/customers/${customerId}/loyalty-points/redeem`,
    payload
  );
  return data;
};

// ─── Public Loyalty Points (for tenant portal, customer-facing) ───

export const fetchMyLoyaltyPoints = async (): Promise<LoyaltyPointsResponse> => {
  const { data } = await apiClient.get<LoyaltyPointsResponse>("/api/customer/loyalty-points");
  return data;
};

export const redeemMyLoyaltyPoints = async (
  payload: RedeemLoyaltyPointsPayload
): Promise<{ success: boolean; transaction: LoyaltyPointTransaction; balance: number }> => {
  const { data } = await apiClient.post<{ success: boolean; transaction: LoyaltyPointTransaction; balance: number }>(
    "/api/customer/loyalty-points/redeem",
    payload
  );
  return data;
};

// ─── Customer Signatures ───

export interface CustomerSignature {
  id: string;
  customer_id: string;
  user_id: string;
  signature_data: string;
  created_at: string;
  updated_at: string;
}

export const fetchCustomerSignature = async (customerId: string): Promise<CustomerSignature | null> => {
  const { data } = await apiClient.get<{ success: boolean; signature: CustomerSignature | null }>(
    `/api/customers/${customerId}/signature`
  );
  return data.signature;
};

export const saveCustomerSignature = async (customerId: string, signatureData: string): Promise<CustomerSignature> => {
  const { data } = await apiClient.put<{ success: boolean; signature: CustomerSignature }>(
    `/api/customers/${customerId}/signature`,
    { signature_data: signatureData }
  );
  return data.signature;
};

export const deleteCustomerSignature = async (customerId: string): Promise<void> => {
  await apiClient.delete(`/api/customers/${customerId}/signature`);
};
